/**
 * ShortcodePreviewPlugin - render {partial: ...} shortcodes inline in TinyMCE
 *
 * Authors write shortcodes like:
 *   {partial: "shared/cms_image", params: {src: "/images/3-2.jpg", alt: "John"}}
 *
 * Left alone, those sit in the editor as a wall of braces. This plugin swaps
 * each one for a non-editable block showing the server-rendered partial, and
 * swaps it back to the raw shortcode on GetContent.
 *
 * Because Save (via editor.save()) and Tools > Source code both flow through
 * getContent(), the single GetContent hook covers both: nothing but the raw
 * shortcode is ever persisted or shown in HTML mode.
 *
 * Mirrors the <!-- drop --> handling in DropZonePlugin.
 */

import {
    parseShortcode,
    serializeShortcode,
    buildDialogItems,
    toDialogData,
    fromDialogData,
    missingRequired,
    attachComboInputs
} from '../utils/SectionForm.js';

const PREVIEW_CLASS = 'wps-shortcode-preview';
const PREVIEW_ENDPOINT = '/admin/shortcodes/preview';

// Must stay in sync with WpsPages::ShortcodeRenderer::PATTERN.
// Non-breaking spaces count as whitespace because editors insert them freely.
const WS = '(?:\\s|&nbsp;|\\u00A0)';

// Matches the opening `{partial: "path"` only. Params are brace-balanced -
// `params: {features: [{icon: "calendar"}]}` nests - and JS regex has no
// recursive subexpression, so the closing brace is found by scanning instead.
const HEAD_SOURCE = `\\{partial:${WS}*["']([^"']+)["']`;

// Build a fresh instance per use rather than sharing one global regex: a `g`
// regex carries lastIndex between calls and silently skips matches when reused.
const headRegex = (flags = 'g') => new RegExp(HEAD_SOURCE, flags);

/**
 * Find every shortcode in `text`, balancing nested braces.
 *
 * Mirrors the Ruby recursive pattern. Returns match-like objects so callers
 * read the same way they did with matchAll: [0] is the full shortcode, [1] the
 * partial path, and `index` its start offset.
 *
 * @returns {Array<{0: string, 1: string, index: number}>}
 */
function findShortcodes(text) {
    const found = [];
    const head = headRegex();
    let match;

    while ((match = head.exec(text))) {
        const end = matchingBrace(text, match.index);

        // Unterminated - leave it as plain text for the author to fix, and
        // resume scanning after the head so a later shortcode still matches.
        if (end === -1) continue;

        found.push({
            0: text.slice(match.index, end + 1),
            1: match[1],
            index: match.index
        });

        head.lastIndex = end + 1;
    }

    return found;
}

/**
 * Index of the `}` closing the `{` at `start`, or -1 if it never closes.
 * Braces inside quoted strings don't count toward the depth.
 */
function matchingBrace(text, start) {
    let depth = 0;
    let quote = null;

    for (let i = start; i < text.length; i++) {
        const char = text[i];

        if (quote) {
            if (char === '\\') i++;
            else if (char === quote) quote = null;
            continue;
        }

        if (char === '"' || char === "'") quote = char;
        else if (char === '{') depth++;
        else if (char === '}' && --depth === 0) return i;
    }

    return -1;
}

// Elements that end a run of text. A shortcode may span inline markup - TinyMCE
// happily drops a <br> into the middle of a long class list - but never a block
// boundary.
const BLOCK_TAGS = new Set([
    'ADDRESS', 'ARTICLE', 'ASIDE', 'BLOCKQUOTE', 'BODY', 'DD', 'DIV', 'DL', 'DT',
    'FIELDSET', 'FIGCAPTION', 'FIGURE', 'FOOTER', 'FORM', 'H1', 'H2', 'H3', 'H4',
    'H5', 'H6', 'HEADER', 'LI', 'MAIN', 'NAV', 'OL', 'P', 'PRE', 'SECTION',
    'TABLE', 'TBODY', 'TD', 'TFOOT', 'TH', 'THEAD', 'TR', 'UL'
]);

// Blocks that may only contain inline content - a preview div inside one is
// invalid and gets restructured on serialization.
const PHRASING_ONLY_TAGS = new Set(['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'DT', 'PRE']);

// `align: 'start'` puts it at the far left of the dialog footer, away from
// Cancel/Save - destructive actions shouldn't sit next to the button people
// click by reflex.
const DELETE_BUTTON = {
    type: 'custom',
    name: 'wpsDeleteSection',
    text: 'Delete',
    align: 'start'
};

// Rendered HTML keyed by the exact shortcode string. Shared across editors -
// the same shortcode always renders the same way.
const renderCache = new Map();

export class ShortcodePreviewPlugin {
    constructor(editor) {
        this.editor = editor;
    }

    init() {
        this.injectStyles();
        this.setupContentProcessing();
        this.setupEditHandler();

        // Process whatever was loaded before the plugin attached.
        this.processBody();
    }

    // ---------------------------------------------------------------- styles

    injectStyles() {
        const styleId = `shortcode-preview-styles-${this.editor.id}`;
        const editorDoc = this.editor.getDoc();
        const targetHead = editorDoc.head || editorDoc.getElementsByTagName('head')[0];

        if (!targetHead || editorDoc.getElementById(styleId)) return;

        const styleEl = editorDoc.createElement('style');
        styleEl.id = styleId;
        styleEl.textContent = `
            .${PREVIEW_CLASS} {
                position: relative;
                outline: 1px dashed transparent;
                outline-offset: 2px;
                transition: outline-color 0.15s ease;
                pointer-events: auto;
            }
            /*
             * Make the preview's contents non-interactive so a click always lands
             * on the wrapper itself.
             *
             * Without this, double-clicking a cms_image preview targets the real
             * <img> inside it, and TinyMCE's built-in image plugin opens its own
             * Image dialog on top of ours — two stacked modals, the lower one
             * inert and impossible to dismiss. Retargeting the event is what stops
             * that plugin firing at all; it checks the double-clicked node, so it
             * must never see an <img>.
             *
             * pointer-events is inherited, so the direct-child rule covers the
             * whole subtree. It also makes a preview behave as the single unit the
             * editor treats it as — no clicking into links or images inside it.
             */
            .${PREVIEW_CLASS} > * {
                pointer-events: none;
            }
            .${PREVIEW_CLASS}:hover {
                outline-color: #a78bfa;
            }
            .${PREVIEW_CLASS}[data-mce-selected] {
                outline: 2px solid #8b5cf6 !important;
            }
            .${PREVIEW_CLASS}:hover::after {
                content: attr(data-partial) " — double-click to edit";
                position: absolute;
                top: 0;
                left: 0;
                z-index: 10;
                padding: 2px 8px;
                font: 500 11px/1.6 ui-sans-serif, system-ui, sans-serif;
                color: #fff;
                background: #8b5cf6;
                border-radius: 0 0 4px 0;
                pointer-events: none;
                white-space: nowrap;
            }
            .${PREVIEW_CLASS}[data-loading="true"] {
                min-height: 48px;
                padding: 12px;
                color: #9ca3af;
                background: #f9fafb;
                border: 1px dashed #d1d5db;
                border-radius: 6px;
                font: 500 13px/1.5 ui-sans-serif, system-ui, sans-serif;
            }
        `;
        targetHead.appendChild(styleEl);
    }

    // ------------------------------------------------------- content hooks

    setupContentProcessing() {
        // Content set into the editor (initial load, source-code dialog save,
        // version restore) arrives with raw shortcodes - render them.
        this.editor.on('SetContent', () => this.processBody());

        // Content read out of the editor (save, textarea sync, source-code
        // dialog open) must contain raw shortcodes, never rendered HTML.
        this.editor.on('GetContent', (e) => {
            e.content = this.cleanupForSave(e.content);
        });
    }

    /**
     * Swap every shortcode in the editor body for a preview block.
     * Runs without dirtying the document or polluting the undo stack.
     */
    processBody() {
        const body = this.editor.getBody();
        if (!body) return;

        const placeholders = this.withoutSideEffects(() => this.replaceShortcodes(body));
        if (placeholders.length) this.renderPlaceholders(placeholders);
    }

    /**
     * Find shortcodes in the body and replace them with preview elements.
     *
     * Text is gathered per block-level container rather than per text node, so
     * a shortcode broken up by inline markup (`items-center<br>&nbsp; gap-2`)
     * still matches. The intervening markup is part of the replaced Range, so
     * it disappears along with the raw shortcode - which is what we want, it
     * was only ever stray formatting inside a parameter value.
     *
     * @returns {HTMLElement[]} newly created, not-yet-rendered preview elements
     */
    replaceShortcodes(body) {
        const created = [];

        this.textSegments(body).forEach((segment) => {
            const text = segment.entries.map((entry) => entry.node.nodeValue).join('');
            const matches = findShortcodes(text);
            if (!matches.length) return;

            // Reverse order: replacing a later match can't invalidate the
            // node/offset positions of an earlier one.
            matches.reverse().forEach((match) => {
                const el = this.replaceRange(
                    segment,
                    match.index,
                    match.index + match[0].length,
                    match[0],
                    match[1],
                    text
                );
                if (el) created.push(el);
            });
        });

        return created;
    }

    /**
     * Group the body's text nodes into runs that share a block-level ancestor.
     * Each entry records where that node starts within its run's text.
     *
     * @returns {Array<{block: Element, entries: Array<{node: Text, start: number}>}>}
     */
    textSegments(body) {
        const doc = this.editor.getDoc();
        const walker = doc.createTreeWalker(body, NodeFilter.SHOW_TEXT);
        const segments = [];

        let current = null;
        let offset = 0;
        let node;

        while ((node = walker.nextNode())) {
            // Don't re-process text inside an existing preview block.
            if (node.parentElement?.closest(`.${PREVIEW_CLASS}`)) continue;

            const block = this.blockAncestor(node, body);
            if (!current || block !== current.block) {
                current = {block, entries: []};
                segments.push(current);
                offset = 0;
            }

            current.entries.push({node, start: offset});
            offset += node.nodeValue.length;
        }

        return segments;
    }

    blockAncestor(node, body) {
        let el = node.parentElement;
        while (el && el !== body && !BLOCK_TAGS.has(el.tagName)) {
            el = el.parentElement;
        }
        return el || body;
    }

    /**
     * Replace [start, end) of a segment's text - which may span several nodes
     * and the markup between them - with a preview element.
     */
    replaceRange(segment, start, end, shortcode, partialPath, segmentText) {
        const doc = this.editor.getDoc();
        const el = this.buildPlaceholder(doc, shortcode, partialPath);

        // A preview holding block-level content can't live inside a <p> or a
        // heading: the serializer splits the wrapper around each block, and
        // every clone carries data-shortcode - so one shortcode multiplies on
        // save. When the whole paragraph is just the shortcode, swap the
        // paragraph itself for the block.
        const {block} = segment;
        if (PHRASING_ONLY_TAGS.has(block.tagName) && segmentText.trim() === shortcode.trim()) {
            block.replaceWith(el);
            return el;
        }

        const startEntry = segment.entries.findLast((entry) => entry.start <= start);
        const endEntry = segment.entries.findLast((entry) => entry.start < end);
        if (!startEntry || !endEntry) return null;

        const range = doc.createRange();
        range.setStart(startEntry.node, start - startEntry.start);
        range.setEnd(endEntry.node, end - endEntry.start);

        range.deleteContents();
        range.insertNode(el);
        return el;
    }

    buildPlaceholder(doc, shortcode, partialPath) {
        // A <div>, not a <span>: partials commonly render block-level content,
        // and an inline wrapper around blocks gets split into one clone per
        // block on serialization - duplicating the shortcode on every save.
        const el = doc.createElement('div');
        el.className = PREVIEW_CLASS;
        el.setAttribute('contenteditable', 'false');
        el.setAttribute('data-mce-contenteditable', 'false');
        el.setAttribute('data-shortcode', this.normalise(shortcode));
        el.setAttribute('data-partial', partialPath);

        const cached = renderCache.get(el.getAttribute('data-shortcode'));
        if (cached !== undefined) {
            el.innerHTML = cached;
        } else {
            el.setAttribute('data-loading', 'true');
            el.textContent = 'Rendering…';
        }
        return el;
    }

    /**
     * Non-breaking spaces are editor debris, never meaningful inside a
     * shortcode. Normalising here means the cleaned-up version is what gets
     * saved back, so the stored content gets tidier over time.
     */
    normalise(shortcode) {
        return shortcode.replace(/\u00A0|&nbsp;/g, ' ');
    }

    /**
     * Fetch HTML for any placeholder that wasn't already cached, in one request.
     */
    async renderPlaceholders(elements) {
        const pending = elements.filter((el) => el.getAttribute('data-loading') === 'true');
        if (!pending.length) return;

        const shortcodes = [...new Set(pending.map((el) => el.getAttribute('data-shortcode')))];
        let results;

        try {
            const response = await fetch(PREVIEW_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
                },
                body: JSON.stringify({shortcodes})
            });
            const data = await response.json();
            if (!data.success) throw new Error(data.error || 'Preview failed');
            results = data.results;
        } catch (error) {
            console.error('❌ Shortcode preview failed:', error);
            this.withoutSideEffects(() => {
                pending.forEach((el) => {
                    el.textContent = el.getAttribute('data-shortcode');
                });
            });
            return;
        }

        Object.entries(results).forEach(([shortcode, html]) => renderCache.set(shortcode, html));

        this.withoutSideEffects(() => {
            pending.forEach((el) => {
                const html = results[el.getAttribute('data-shortcode')];
                if (html === undefined) return;
                el.removeAttribute('data-loading');
                el.innerHTML = html;
            });
        });
    }

    /**
     * Replace preview elements with their raw shortcode text.
     * @param {string} html serialized editor content
     */
    cleanupForSave(html) {
        if (!html || !html.includes(PREVIEW_CLASS)) return html;

        const temp = document.createElement('div');
        temp.innerHTML = html;

        temp.querySelectorAll(`.${PREVIEW_CLASS}`).forEach((el) => {
            const shortcode = el.getAttribute('data-shortcode');
            el.parentNode.replaceChild(
                document.createTextNode(shortcode || ''),
                el
            );
        });

        return temp.innerHTML;
    }

    // -------------------------------------------------------------- editing

    /**
     * Open the settings dialog when a preview is double-clicked.
     *
     * Bound natively on the iframe document in the CAPTURE phase rather than via
     * editor.on('dblclick'), because TinyMCE's own `image` plugin also opens a
     * dialog on double-click and a cms_image preview contains a real <img>.
     * Plugin handlers are bound before this plugin initialises (it runs inside
     * editor.on('init')), so by the time an editor.on('dblclick') handler runs
     * TinyMCE's has already fired — preventDefault/stopImmediatePropagation there
     * are both too late. Capturing on the document gets in first, so the event
     * never reaches TinyMCE and only one dialog ever opens.
     */
    setupEditHandler() {
        this.onDoubleClick = (e) => {
            const el = e.target.closest?.(`.${PREVIEW_CLASS}`);
            if (!el) return;

            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            this.openEditDialog(el);
        };

        const bind = () => {
            const doc = this.editor.getDoc();
            // Falling back to editor.on('dblclick') would reintroduce the stacked
            // dialog, so if there is no document yet, wait for one rather than
            // binding the wrong way.
            if (!doc || doc === this.boundDoc) return;

            doc.addEventListener('dblclick', this.onDoubleClick, true);
            this.boundDoc = doc;
        };

        bind();
        this.editor.on('init', bind);
        this.editor.on('SetContent', bind);

        this.editor.on('remove', () => {
            this.boundDoc?.removeEventListener('dblclick', this.onDoubleClick, true);
            this.boundDoc = null;
        });
    }

    /**
     * Prefer a form built from the section manifest; fall back to editing the raw
     * shortcode when the partial has no declared schema (or the lookup fails), so
     * unlisted partials keep working exactly as before.
     */
    async openEditDialog(el) {
        // There is an await before the dialog opens, so a second double-click in
        // that window would stack a second dialog. TinyMCE renders the older one
        // inert behind the new one with no way to dismiss it.
        if (this.dialogPending) return;
        this.dialogPending = true;

        const original = el.getAttribute('data-shortcode') || '';

        let parsed = null;
        try {
            parsed = await parseShortcode(original);
        } catch (error) {
            // Network or server problem - fall through to the raw dialog below.
        } finally {
            this.dialogPending = false;
        }

        if (parsed?.success && parsed.schema) {
            // Deliberately outside the catch above. Wrapping the open() call in
            // the same try meant a rendering error inside the schema dialog fell
            // through to openRawDialog, stacking a second modal on top of a
            // half-built first one that could no longer be dismissed. If the
            // schema dialog fails now, say so and stop.
            try {
                this.openSchemaDialog(el, parsed);
                return;
            } catch (error) {
                this.closeStrandedDialog();
                this.editor.notificationManager.open({
                    text: `Could not open the settings for this section: ${error.message}`,
                    type: 'error',
                    timeout: 5000
                });
                return;
            }
        }

        this.openRawDialog(el, original);
    }

    /**
     * Remove a dialog that died partway through rendering.
     *
     * TinyMCE leaves the partially-built element in the DOM when open() throws,
     * and it has no footer buttons and no registered close handler - so there is
     * no way for anyone to dismiss it from the UI.
     */
    closeStrandedDialog() {
        try {
            this.editor.windowManager.close();
        } catch (error) {
            // Nothing registered to close; fall through to the DOM sweep.
        }

        document.querySelectorAll('.tox-dialog').forEach((dialog) => {
            if (!dialog.querySelector('.tox-dialog__footer button')) {
                dialog.closest('.tox-dialog-wrap')?.remove() ?? dialog.remove();
            }
        });
    }

    openSchemaDialog(el, parsed) {
        const schemaParams = parsed.schema.params || [];

        this.editor.windowManager.open({
            title: parsed.schema.label || 'Edit section',
            size: 'large',
            body: {
                type: 'panel',
                items: buildDialogItems(schemaParams, { description: parsed.schema.description })
            },
            initialData: toDialogData(schemaParams, parsed.params || {}),
            buttons: [
                DELETE_BUTTON,
                { type: 'cancel', text: 'Cancel' },
                { type: 'submit', text: 'Save', primary: true }
            ],
            onAction: (api, details) => this.handleDeleteAction(api, details, el),
            onSubmit: async (api) => {
                const params = fromDialogData(schemaParams, api.getData());
                const missing = missingRequired(schemaParams, params);

                if (missing.length > 0) {
                    this.editor.notificationManager.open({
                        text: `Please fill in: ${missing.join(', ')}`,
                        type: 'warning',
                        timeout: 4000
                    });
                    return;
                }

                try {
                    const result = await serializeShortcode(parsed.partial, params);
                    if (!result.success) throw new Error(result.error || 'Could not save the section');
                    api.close();
                    this.replaceShortcode(el, result.shortcode);
                } catch (error) {
                    this.editor.notificationManager.open({
                        text: error.message,
                        type: 'error',
                        timeout: 4000
                    });
                }
            }
        });

        // After render: the dialog spec can't set an input's `list` attribute, so
        // combo fields are joined to their datalist once they exist in the DOM.
        requestAnimationFrame(() => attachComboInputs(schemaParams));
    }

    openRawDialog(el, original) {
        this.editor.windowManager.open({
            title: 'Edit partial block',
            size: 'large',
            body: {
                type: 'panel',
                items: [
                    {
                        type: 'textarea',
                        name: 'shortcode',
                        label: 'Shortcode',
                        maximized: true
                    }
                ]
            },
            initialData: {shortcode: original},
            buttons: [
                DELETE_BUTTON,
                {type: 'cancel', text: 'Cancel'},
                {type: 'submit', text: 'Save', primary: true}
            ],
            onAction: (api, details) => this.handleDeleteAction(api, details, el),
            onSubmit: (api) => {
                const updated = (api.getData().shortcode || '').trim();
                api.close();
                if (!updated || updated === original) return;
                this.replaceShortcode(el, updated);
            }
        });
    }

    /**
     * Footer "Delete" handler shared by both dialogs.
     */
    handleDeleteAction(api, details, el) {
        if (details.name !== DELETE_BUTTON.name) return;
        api.close();
        this.removeShortcode(el);
    }

    /**
     * Remove a section from the page.
     *
     * Wrapped in an undo transaction (unlike the display-only preview swaps) so a
     * mis-click is one Ctrl+Z away — which is why there's no confirmation prompt.
     */
    removeShortcode(el) {
        this.editor.undoManager.transact(() => el.remove());
        this.editor.setDirty(true);
        this.editor.save();
    }

    replaceShortcode(el, shortcode) {
        const [match] = findShortcodes(shortcode);

        // Not a valid shortcode any more - drop back to plain text so the
        // author can see and fix what they typed.
        if (!match || match[0] !== shortcode) {
            el.parentNode.replaceChild(this.editor.getDoc().createTextNode(shortcode), el);
            this.editor.setDirty(true);
            this.editor.save();
            return;
        }

        el.setAttribute('data-shortcode', this.normalise(shortcode));
        el.setAttribute('data-partial', match[1]);

        const cached = renderCache.get(el.getAttribute('data-shortcode'));
        if (cached !== undefined) {
            el.removeAttribute('data-loading');
            el.innerHTML = cached;
        } else {
            el.setAttribute('data-loading', 'true');
            el.textContent = 'Rendering…';
            this.renderPlaceholders([el]);
        }

        this.editor.setDirty(true);
        this.editor.save();
    }

    // --------------------------------------------------------------- helpers

    /**
     * Run a DOM mutation without adding an undo level or marking the document
     * dirty - swapping in a preview is a display concern, not an edit.
     */
    withoutSideEffects(fn) {
        const wasDirty = this.editor.isDirty();
        let result;
        this.editor.undoManager.ignore(() => {
            result = fn();
        });
        if (!wasDirty) this.editor.setDirty(false);
        return result;
    }
}

export default ShortcodePreviewPlugin;
